import { useEffect } from 'react';
import { useAppShell, type FabAction } from './AppShellContext';
import { cn } from '@/lib/utils';

/**
 * Mobile-only floating action button. Hidden on `md:` and up.
 *
 * Pages register their primary action via `useRegisterFab(action)`;
 * the AppShell renders this single instance. Phase 1.5 ships the
 * primitive; per-route actions land in subsequent phases (e.g.
 * Phase 3 wires "Log this run" on the checklist run page).
 */
export default function Fab() {
  const { fab } = useAppShell();
  if (!fab) return null;
  return (
    <button
      type="button"
      onClick={fab.onClick}
      aria-label={fab.label}
      className={cn(
        'fixed right-5 z-30 flex h-14 w-14 items-center justify-center rounded-full md:hidden',
        // Sit above the bottom nav (~64px tall) plus its safe-area
        // inset; another 0.75rem of breathing room. The mobile FAB is
        // the only ever-visible action affordance, so its position
        // matters.
        'bottom-[calc(72px+env(safe-area-inset-bottom))]',
        'bg-warning text-warning-foreground shadow-lg ring-4 ring-background',
        'transition-transform active:scale-95',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      )}
    >
      {fab.icon}
    </button>
  );
}

/**
 * Page-side hook. Registers the primary action for the page's
 * lifetime; clears it on unmount.
 *
 * Stable identity is the caller's responsibility — pass a
 * memoized `action` (via `useMemo`) if any of its fields close over
 * mutable state.
 */
export function useRegisterFab(action: FabAction | null) {
  const { setFab } = useAppShell();
  useEffect(() => {
    setFab(action);
    return () => setFab(null);
  }, [action, setFab]);
}
